import { useEffect, useState } from "react";
import { UsersRound, Plus, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, goldBtn } from "@/pages/_shared";

const ROLES = ["owner", "director", "manager", "hr", "finance", "operations", "recruitment", "supervisor", "employee", "contractor"];

export default function Team() {
  const { can } = useOrg();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "employee" });

  const load = async () => { try { const { data } = await api.get("/invites"); setMembers(data); } catch (e) { setMembers([]); } };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      const { data } = await api.post("/invites", { email: form.email, role: form.role });
      toast.success("Invitation sent");
      if (data.invite_link) { navigator.clipboard.writeText(data.invite_link); toast("Invite link copied"); }
      setOpen(false); setForm({ email: "", role: "employee" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };
  const changeRole = async (id, role) => { await api.patch(`/members/${id}/role`, { role }); toast.success("Role updated"); load(); };

  if (members === null) return <Spinner />;

  return (
    <Scroller>
      <PageHeader title="Team" subtitle="Invite staff and manage roles & permissions" icon={UsersRound}
        action={can("users.manage") && <button onClick={() => setOpen(true)} className={goldBtn} data-testid="team-invite-btn"><Plus className="h-4 w-4" /> Invite</button>} />
      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-charcoal px-4 py-3" data-testid="team-member">
            <div>
              <p className="text-sm font-medium text-white/85">{m.name || m.email}</p>
              <p className="text-xs text-white/40">{m.email} · <span className={m.status === "active" ? "text-emerald-400" : "text-gold/70"}>{m.status}</span></p>
            </div>
            {can("users.manage") ? (
              <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} className="rounded-lg border border-white/10 bg-graphite px-3 py-1.5 text-xs capitalize text-white/70 focus:outline-none" data-testid="team-role-select">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : <span className="text-xs capitalize text-gold/70">{m.role}</span>}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-charcoal p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Invite a team member</h2><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-white/40" /></button></div>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" type="email" data-testid="invite-email"
              className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role"
              className="mb-4 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm capitalize text-white/80 focus:outline-none">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={invite} className={`${goldBtn} w-full justify-center`} data-testid="invite-send"><Copy className="h-4 w-4" /> Send Invitation</button>
          </div>
        </div>
      )}
    </Scroller>
  );
}
